import React, { useState, useEffect, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  Pencil,
  Trash,
  SpinnerGap,
  MagnifyingGlass,
  ChartBar,
  Database,
  Warning,
  Users,
  Download,
  Megaphone,
  PaperPlaneTilt,
  Eye
} from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Defects & Dimensions', 'Drivers\' Hours & Tachographs', 'Driver Licensing', 'Insurance', 'Driver CPC', 'Goods Vehicle Operator Licensing', 'PSV Operator Licensing', 'Overloading', 'Plating & Testing', 'Dangerous Goods', 'Vehicle Excise & Registration'];
const VEHICLE_TYPES = ['HGV', 'PSV', 'LGV'];
const SEVERITIES = ['MSI', 'VSI', 'SI', 'Standard'];

export const AdminPage = () => {
  const [sanctions, setSanctions] = useState([]);
  const [sanctionFilter, setSanctionFilter] = useState('all'); // 'all' | 'pending'
  const [loading, setLoading] = useState(true);
  const [searchAnalytics, setSearchAnalytics] = useState([]);
  const [viewAnalytics, setViewAnalytics] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteUserConfirm, setDeleteUserConfirm] = useState('');

  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [audienceCounts, setAudienceCounts] = useState({});
  const [unsubscribedCount, setUnsubscribedCount] = useState(0);
  const [annSubject, setAnnSubject] = useState('');
  const [annBody, setAnnBody] = useState('');
  const [annAudience, setAnnAudience] = useState('active');
  const [annPreview, setAnnPreview] = useState(false);
  const [annSending, setAnnSending] = useState(false);
  const [annResult, setAnnResult] = useState(null);
  
  const [editingSanction, setEditingSanction] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    offence_title: '',
    category: '',
    vehicle_type: '',
    severity: '',
    description: '',
    likely_outcome: '',
    notes: ''
  });

  const fetchSanctions = useCallback(async () => {
    try {
      // limit=500 so unverified Dangerous Goods (49) appear alongside other categories
      const { data } = await axios.get(`${API}/api/admin/sanctions?limit=500`);
      setSanctions(data.sanctions || []);
    } catch (error) {
      console.error('Error fetching sanctions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [searchRes, viewRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/analytics/searches?days=7`),
        axios.get(`${API}/api/analytics/views?days=7`),
        axios.get(`${API}/api/analytics/dashboard-stats`)
      ]);
      setSearchAnalytics(searchRes.data.top_searches || []);
      setViewAnalytics(viewRes.data.top_viewed || []);
      setDashboardStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/users`);
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const handleDeleteUser = async () => {
    if (!deletingUser || deleteUserConfirm !== 'DELETE') return;
    try {
      await axios.delete(`${API}/api/admin/users/${encodeURIComponent(deletingUser.email)}`);
      setDeletingUser(null);
      setDeleteUserConfirm('');
      fetchUsers();
      fetchAnalytics();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleExportUsers = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/users/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('download', `vantage-users-${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting users:', error);
    }
  };

  const fetchAnnouncements = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/announcements`);
      setAnnouncements(data.announcements || []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  }, []);

  const handleVerifySanction = async (sanctionId) => {
    try {
      await axios.post(`${API}/api/admin/sanctions/${sanctionId}/verify`);
      fetchSanctions();
    } catch (error) {
      console.error('Error verifying sanction:', error);
      alert(error.response?.data?.detail || 'Failed to verify sanction');
    }
  };

  const fetchAudienceCounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/announcements/audience-counts`);
      setAudienceCounts(data.counts || {});
      setUnsubscribedCount(data.unsubscribed_count || 0);
    } catch (error) {
      console.error('Error fetching audience counts:', error);
    }
  }, []);

  const handleSendAnnouncement = async () => {
    if (!annSubject.trim() || !annBody.trim()) return;
    const recipients = audienceCounts[annAudience] || 0;
    if (recipients === 0) {
      alert('No recipients match this audience.');
      return;
    }
    if (!window.confirm(`Send "${annSubject}" to ${recipients} recipient(s)?`)) return;
    setAnnSending(true);
    setAnnResult(null);
    try {
      const { data } = await axios.post(`${API}/api/admin/announcements`, {
        subject: annSubject,
        body_html: annBody,
        audience: annAudience,
      });
      setAnnResult(data);
      setAnnSubject('');
      setAnnBody('');
      setAnnPreview(false);
      // Refresh list after a short delay to let background task start
      setTimeout(() => fetchAnnouncements(), 1500);
    } catch (error) {
      setAnnResult({ error: error.response?.data?.detail || 'Failed to send announcement' });
    } finally {
      setAnnSending(false);
    }
  };

  useEffect(() => {
    fetchSanctions();
    fetchAnalytics();
    fetchUsers();
    fetchAnnouncements();
    fetchAudienceCounts();
  }, [fetchSanctions, fetchAnalytics, fetchUsers, fetchAnnouncements, fetchAudienceCounts]);

  const resetForm = () => {
    setFormData({
      offence_title: '',
      category: '',
      vehicle_type: '',
      severity: '',
      description: '',
      likely_outcome: '',
      notes: ''
    });
    setEditingSanction(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (sanction) => {
    setFormData({
      offence_title: sanction.offence_title || '',
      category: sanction.category || '',
      vehicle_type: sanction.vehicle_type || '',
      severity: sanction.severity || '',
      description: sanction.description || '',
      likely_outcome: sanction.likely_outcome || '',
      notes: sanction.notes || ''
    });
    setEditingSanction(sanction);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingSanction) {
        await axios.put(
          `${API}/api/admin/sanctions/${editingSanction.id}`,
          formData,
        );
      } else {
        await axios.post(
          `${API}/api/admin/sanctions`,
          formData,
        );
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchSanctions();
    } catch (error) {
      console.error('Error saving sanction:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sanction?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/sanctions/${id}`);
      fetchSanctions();
    } catch (error) {
      console.error('Error deleting sanction:', error);
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'MSI':
        return 'severity-serious';
      case 'VSI':
        return 'severity-significant';
      case 'SI':
        return 'severity-significant';
      case 'STANDARD':
        return 'severity-minor';
      default:
        return 'bg-muted';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">Admin Panel</h1>
            <p className="text-muted-foreground">Manage sanctions data and view analytics</p>
          </div>
        </div>
        
        <Tabs defaultValue="sanctions" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-2xl">
            <TabsTrigger value="sanctions" className="flex items-center gap-2">
              <Database weight="duotone" className="h-4 w-4" />
              Sanctions
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2" data-testid="users-tab">
              <Users weight="duotone" className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="announcements" className="flex items-center gap-2" data-testid="announcements-tab">
              <Megaphone weight="duotone" className="h-4 w-4" />
              Email
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <ChartBar weight="duotone" className="h-4 w-4" />
              Analytics
            </TabsTrigger>
          </TabsList>
          
          {/* Sanctions Tab */}
          <TabsContent value="sanctions" className="space-y-4">
            <div className="flex justify-end">
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openCreateDialog} className="rounded-sm" data-testid="add-sanction-btn">
                    <Plus weight="bold" className="mr-2 h-4 w-4" />
                    Add Sanction
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingSanction ? 'Edit Sanction' : 'Add New Sanction'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="offence_title">Offence Title *</Label>
                      <Input
                        id="offence_title"
                        value={formData.offence_title}
                        onChange={(e) => setFormData({ ...formData, offence_title: e.target.value })}
                        required
                        className="rounded-sm"
                        data-testid="offence-title-input"
                      />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Category *</Label>
                        <Select 
                          value={formData.category} 
                          onValueChange={(v) => setFormData({ ...formData, category: v })}
                          required
                        >
                          <SelectTrigger className="rounded-sm" data-testid="category-select">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Vehicle Type *</Label>
                        <Select 
                          value={formData.vehicle_type} 
                          onValueChange={(v) => setFormData({ ...formData, vehicle_type: v })}
                          required
                        >
                          <SelectTrigger className="rounded-sm" data-testid="vehicle-type-select">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {VEHICLE_TYPES.map((v) => (
                              <SelectItem key={v} value={v}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Severity *</Label>
                        <Select 
                          value={formData.severity} 
                          onValueChange={(v) => setFormData({ ...formData, severity: v })}
                          required
                        >
                          <SelectTrigger className="rounded-sm" data-testid="severity-select">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {SEVERITIES.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="description">Description *</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        required
                        rows={3}
                        className="rounded-sm"
                        data-testid="description-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="likely_outcome">Likely Outcome *</Label>
                      <Textarea
                        id="likely_outcome"
                        value={formData.likely_outcome}
                        onChange={(e) => setFormData({ ...formData, likely_outcome: e.target.value })}
                        required
                        rows={3}
                        className="rounded-sm"
                        data-testid="outcome-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes (optional)</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={2}
                        className="rounded-sm"
                        data-testid="notes-input"
                      />
                    </div>
                    
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">
                        Cancel
                      </Button>
                      <Button type="submit" disabled={saving} className="rounded-sm" data-testid="save-sanction-btn">
                        {saving ? (
                          <>
                            <SpinnerGap className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          editingSanction ? 'Update' : 'Create'
                        )}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Pending review notice */}
            {sanctions.some((s) => s.verified === false) && (
              <div className="border border-amber-300/60 bg-amber-50 dark:bg-amber-900/15 dark:border-amber-800 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="pending-review-notice">
                <div>
                  <p className="font-semibold text-amber-900 dark:text-amber-300">
                    {sanctions.filter((s) => s.verified === false).length} sanction{sanctions.filter((s) => s.verified === false).length === 1 ? '' : 's'} awaiting review
                  </p>
                  <p className="text-sm text-amber-800 dark:text-amber-400">
                    These were imported from the May 2026 policy and are hidden from subscribers until you approve each one (or click Approve All).
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSanctionFilter(sanctionFilter === 'pending' ? 'all' : 'pending')}
                    className="rounded-sm"
                    data-testid="toggle-pending-filter-btn"
                  >
                    {sanctionFilter === 'pending' ? 'Show all' : 'Show pending only'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={async () => {
                      const pending = sanctions.filter((s) => s.verified === false);
                      if (!window.confirm(`Approve all ${pending.length} pending sanctions?`)) return;
                      for (const s of pending) {
                        await axios.post(`${API}/api/admin/sanctions/${s.id}/verify`);
                      }
                      fetchSanctions();
                    }}
                    className="rounded-sm"
                    data-testid="approve-all-pending-btn"
                  >
                    Approve all
                  </Button>
                </div>
              </div>
            )}

            {/* Sanctions Table */}
            <div className="border border-border">
              {loading ? (
                <div className="p-12 text-center">
                  <SpinnerGap weight="bold" className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Offence</TableHead>
                      <TableHead className="hidden md:table-cell">Category</TableHead>
                      <TableHead className="hidden sm:table-cell">Vehicle</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(sanctionFilter === 'pending' ? sanctions.filter((s) => s.verified === false) : sanctions).map((sanction) => (
                      <TableRow key={sanction.id} className={sanction.verified === false ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''}>
                        <TableCell className="font-medium max-w-xs truncate">
                          <div className="flex items-center gap-2">
                            {sanction.verified === false && (
                              <Badge variant="secondary" className="rounded-sm bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-[10px]" data-testid="pending-review-badge">
                                PENDING
                              </Badge>
                            )}
                            <span className="truncate">{sanction.offence_title}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">{sanction.category}</TableCell>
                        <TableCell className="hidden sm:table-cell">{sanction.vehicle_type}</TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getSeverityClass(sanction.severity)}`}>
                            {sanction.severity}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {sanction.verified === false && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleVerifySanction(sanction.id)}
                                className="rounded-sm h-8"
                                data-testid={`verify-sanction-${sanction.id}`}
                              >
                                Approve
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => openEditDialog(sanction)}
                              data-testid={`edit-sanction-${sanction.id}`}
                            >
                              <Pencil weight="duotone" className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => handleDelete(sanction.id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-sanction-${sanction.id}`}
                            >
                              <Trash weight="duotone" className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
            
            <p className="text-sm text-muted-foreground">
              Total: {sanctions.length} sanctions
            </p>
          </TabsContent>
          
          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email or name..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-9 rounded-sm"
                  data-testid="user-search-input"
                />
              </div>
              <Button onClick={handleExportUsers} variant="outline" className="rounded-sm" data-testid="export-users-btn">
                <Download weight="bold" className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>

            <div className="border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead className="hidden md:table-cell">Name</TableHead>
                    <TableHead>Subscription</TableHead>
                    <TableHead className="hidden lg:table-cell">Registered</TableHead>
                    <TableHead className="hidden lg:table-cell">Last Login</TableHead>
                    <TableHead className="hidden xl:table-cell">Source</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users
                    .filter((u) => {
                      if (!userSearch) return true;
                      const q = userSearch.toLowerCase();
                      return u.email?.toLowerCase().includes(q) || u.name?.toLowerCase().includes(q);
                    })
                    .map((u) => (
                      <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                        <TableCell className="font-medium">
                          {u.email}
                          {u.role === 'admin' && (
                            <Badge variant="secondary" className="ml-2 rounded-sm text-[10px]">ADMIN</Badge>
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground">{u.name || '—'}</TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={`rounded-sm ${
                              u.subscription_status === 'active'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : u.subscription_status === 'cancelled'
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                                : ''
                            }`}
                          >
                            {u.subscription_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                          {u.created_at ? u.created_at.slice(0, 10) : '—'}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                          {u.last_login ? u.last_login.slice(0, 10) : 'Never'}
                        </TableCell>
                        <TableCell className="hidden xl:table-cell text-xs text-muted-foreground" title={u.referral_source === 'other' && u.referral_source_other ? u.referral_source_other : ''}>
                          {u.referral_source === 'other' && u.referral_source_other
                            ? `Other: ${u.referral_source_other}`
                            : u.referral_source
                              ? u.referral_source.replace(/_/g, ' ')
                              : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeletingUser(u)}
                            className="text-destructive hover:text-destructive"
                            disabled={u.role === 'admin'}
                            aria-label={`Delete user ${u.email}`}
                            data-testid={`delete-user-${u.email}`}
                          >
                            <Trash weight="duotone" className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-sm text-muted-foreground">
              Total: {users.length} registered {users.length === 1 ? 'account' : 'accounts'}
            </p>
          </TabsContent>
          
          {/* Announcements / Email Tab */}
          <TabsContent value="announcements" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Compose */}
              <div className="lg:col-span-2 border border-border">
                <div className="p-4 border-b border-border flex items-center gap-2">
                  <Megaphone weight="duotone" className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">Send Announcement</h3>
                </div>
                <div className="p-4 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="ann-subject">Subject</Label>
                    <Input
                      id="ann-subject"
                      placeholder="New sanctions update for HGV operators"
                      value={annSubject}
                      onChange={(e) => setAnnSubject(e.target.value)}
                      className="rounded-sm"
                      data-testid="announcement-subject-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ann-audience">Audience</Label>
                    <Select value={annAudience} onValueChange={setAnnAudience}>
                      <SelectTrigger className="rounded-sm" data-testid="announcement-audience-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active subscribers ({audienceCounts.active ?? '…'})</SelectItem>
                        <SelectItem value="active_and_cancelled">Active + Cancelled ({audienceCounts.active_and_cancelled ?? '…'})</SelectItem>
                        <SelectItem value="cancelled">Cancelled only ({audienceCounts.cancelled ?? '…'})</SelectItem>
                        <SelectItem value="inactive">Never-subscribed ({audienceCounts.inactive ?? '…'})</SelectItem>
                        <SelectItem value="all">All registered users ({audienceCounts.all ?? '…'})</SelectItem>
                      </SelectContent>
                    </Select>
                    {unsubscribedCount > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {unsubscribedCount} user{unsubscribedCount === 1 ? '' : 's'} unsubscribed and will be skipped automatically.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ann-body">Message (HTML supported)</Label>
                    <Textarea
                      id="ann-body"
                      placeholder={'<p>Hi,</p>\n<p>We\'ve just added new offences covering ...</p>\n<p>Best,<br/>The Vantage team</p>'}
                      value={annBody}
                      onChange={(e) => setAnnBody(e.target.value)}
                      rows={10}
                      className="rounded-sm font-mono text-sm"
                      data-testid="announcement-body-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Your message is wrapped in Vantage-branded header/footer with a one-click unsubscribe link (GDPR-compliant).
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setAnnPreview(!annPreview)}
                      className="rounded-sm"
                      disabled={!annSubject.trim() || !annBody.trim()}
                      data-testid="preview-announcement-btn"
                    >
                      <Eye weight="duotone" className="mr-2 h-4 w-4" />
                      {annPreview ? 'Hide Preview' : 'Preview'}
                    </Button>
                    <Button
                      onClick={handleSendAnnouncement}
                      disabled={annSending || !annSubject.trim() || !annBody.trim()}
                      className="rounded-sm"
                      data-testid="send-announcement-btn"
                    >
                      {annSending ? (
                        <><SpinnerGap className="mr-2 h-4 w-4 animate-spin" /> Queuing…</>
                      ) : (
                        <><PaperPlaneTilt weight="duotone" className="mr-2 h-4 w-4" /> Send to {audienceCounts[annAudience] ?? 0} recipient{(audienceCounts[annAudience] ?? 0) === 1 ? '' : 's'}</>
                      )}
                    </Button>
                  </div>

                  {annResult && !annResult.error && (
                    <div className="p-3 border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 text-sm text-green-800 dark:text-green-300" data-testid="announcement-success">
                      Queued — delivering to {annResult.recipient_count} recipient{annResult.recipient_count === 1 ? '' : 's'} in the background.
                    </div>
                  )}
                  {annResult?.error && (
                    <div className="p-3 border border-destructive/30 bg-destructive/5 text-sm text-destructive">
                      {annResult.error}
                    </div>
                  )}

                  {annPreview && (
                    <div className="border border-border p-4 bg-muted/30" data-testid="announcement-preview">
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Preview</p>
                      <div className="border border-border bg-background p-4">
                        <h2 className="text-primary font-semibold">Vantage Fleet &amp; Compliance</h2>
                        <p className="text-xs text-muted-foreground mb-3">Roadworthiness Guide</p>
                        <hr className="border-primary mb-4" />
                        <h3 className="font-semibold mb-3">{annSubject || '(subject)'}</h3>
                        <div
                          className="prose prose-sm max-w-none dark:prose-invert"
                          dangerouslySetInnerHTML={{ __html: annBody || '<em>(empty)</em>' }}
                        />
                        <hr className="my-6" />
                        <p className="text-xs text-muted-foreground">
                          You received this email because you registered for Vantage Fleet &amp; Compliance.
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          <u>Unsubscribe from announcements</u>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent announcements */}
              <div className="border border-border">
                <div className="p-4 border-b border-border">
                  <h3 className="font-semibold">Recent</h3>
                </div>
                <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
                  {announcements.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No announcements sent yet.</p>
                  ) : (
                    announcements.map((a) => (
                      <div key={a.id} className="border border-border p-3" data-testid={`announcement-${a.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-sm font-medium truncate pr-2" title={a.subject}>{a.subject}</p>
                          <Badge
                            variant="secondary"
                            className={`rounded-sm text-[10px] ${a.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : ''}`}
                          >
                            {a.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-1">
                          {a.audience.replace(/_/g, ' ')} · {a.created_at?.slice(0, 10)}
                        </p>
                        <p className="text-xs">
                          <span className="text-green-700 dark:text-green-400 font-medium">{a.sent_count}</span>
                          {' sent'}
                          {a.failed_count > 0 && (
                            <>
                              {' · '}
                              <span className="text-destructive">{a.failed_count} failed</span>
                            </>
                          )}
                          {' · '}{a.recipient_count} total
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            {/* User Stats Cards */}
            {dashboardStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="border border-border p-4">
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Total Users</p>
                  <p className="text-3xl font-bold" data-testid="stat-total-users">{dashboardStats.total_users}</p>
                </div>
                <div className="border border-border p-4">
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Active Subscribers</p>
                  <p className="text-3xl font-bold text-green-600" data-testid="stat-active-subs">{dashboardStats.active_subscribers}</p>
                </div>
                <div className="border border-border p-4">
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Cancelled</p>
                  <p className="text-3xl font-bold text-amber-600">{dashboardStats.cancelled_subscribers}</p>
                </div>
                <div className="border border-border p-4">
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Logins (30 days)</p>
                  <p className="text-3xl font-bold" data-testid="stat-logins">{dashboardStats.total_logins_30d}</p>
                </div>
              </div>
            )}

            {/* User Login Frequency */}
            {dashboardStats?.user_logins?.length > 0 && (
              <div className="border border-border">
                <div className="p-4 border-b border-border flex items-center gap-2">
                  <ChartBar weight="duotone" className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">User Login Frequency (30 days)</h3>
                </div>
                <div className="p-4">
                  <div className="space-y-2">
                    {dashboardStats.user_logins.map((item, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                        <span className="text-sm truncate max-w-[250px]">{item.email}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground">{item.last_login?.slice(0, 10)}</span>
                          <Badge variant="secondary" className="rounded-sm">{item.login_count} logins</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top Searches */}
              <div className="border border-border">
                <div className="p-4 border-b border-border flex items-center gap-2">
                  <MagnifyingGlass weight="duotone" className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">Top Searches (7 days)</h3>
                </div>
                <div className="p-4">
                  {searchAnalytics.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No search data yet</p>
                  ) : (
                    <div className="space-y-2">
                      {searchAnalytics.map((item, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                          <span className="text-sm truncate max-w-[200px]">"{item.query}"</span>
                          <Badge variant="secondary" className="rounded-sm">{item.count}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Most Viewed */}
              <div className="border border-border">
                <div className="p-4 border-b border-border flex items-center gap-2">
                  <ChartBar weight="duotone" className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">Most Viewed (7 days)</h3>
                </div>
                <div className="p-4">
                  {viewAnalytics.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No view data yet</p>
                  ) : (
                    <div className="space-y-2">
                      {viewAnalytics.map((item, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                          <span className="text-sm truncate max-w-[200px]">{item.title}</span>
                          <Badge variant="secondary" className="rounded-sm">{item.count}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={!!deletingUser} onOpenChange={(o) => { if (!o) { setDeletingUser(null); setDeleteUserConfirm(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete User Account</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            You are about to permanently delete <strong>{deletingUser?.email}</strong>. This will remove the user, their payment history, and all analytics associated with their account. This action cannot be undone. Type <strong>DELETE</strong> to confirm.
          </p>
          <Input
            value={deleteUserConfirm}
            onChange={(e) => setDeleteUserConfirm(e.target.value)}
            placeholder="Type DELETE to confirm"
            className="rounded-sm"
            data-testid="delete-user-confirm-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeletingUser(null); setDeleteUserConfirm(''); }} className="rounded-sm">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleteUserConfirm !== 'DELETE'}
              className="rounded-sm"
              data-testid="confirm-delete-user-btn"
            >
              Permanently Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
